import { useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useCachedData } from './useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { useToast } from '../context/ToastContext';

export interface ContentLink {
  id: string;
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface LinkFilter {
  sourceType?: string;
  targetType?: string;
  sourceId?: string;
  targetId?: string;
  metadata?: Record<string, any>;
}

export function useContentLinks() {
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const { showToast } = useToast();
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  // Create a single link
  const createLink = async (
    sourceType: string,
    sourceId: string,
    targetType: string,
    targetId: string,
    metadata?: Record<string, any>
  ): Promise<ContentLink | null> => {
    if (!user) return null;

    setIsLinking(true);
    try {
      const { data, error } = await supabase
        .from('content_links')
        .insert({
          source_type: sourceType,
          source_id: sourceId,
          target_type: targetType,
          target_id: targetId,
          user_id: user.id,
          metadata: metadata || {}
        })
        .select()
        .single();

      if (error) throw error;

      // Invalidate relevant caches
      await invalidateCache(`${CACHE_KEYS.CONTENT_LINKS}_*`);
      if (targetType === 'career_focus') {
        await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_*`);
      }
      
      return data;
    } catch (error: any) {
      console.error('Error creating link:', error);
      if (error?.code === '23505') {
        showToast('This item is already linked', 'error');
      } else {
        showToast('Failed to create link', 'error');
      }
      return null;
    } finally {
      setIsLinking(false);
    }
  };

  // Create multiple links in bulk
  const createBulkLinks = async (
    links: Array<{
      sourceType: string;
      sourceId: string;
      targetType: string;
      targetId: string;
      metadata?: Record<string, any>;
    }>
  ): Promise<ContentLink[]> => {
    if (!user || links.length === 0) return [];

    setIsLinking(true);
    try {
      const linkData = links.map(link => ({
        source_type: link.sourceType,
        source_id: link.sourceId,
        target_type: link.targetType,
        target_id: link.targetId,
        user_id: user.id,
        metadata: link.metadata || {}
      }));

      const { data, error } = await supabase
        .from('content_links')
        .insert(linkData)
        .select();

      if (error) {
        console.error('Insert error details:', error);
        throw error;
      }

      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.CONTENT_LINKS}_*`);
      await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_*`);
      
      showToast(`Successfully linked ${data.length} items`, 'success');
      return data || [];
    } catch (error) {
      console.error('Error creating bulk links:', error);
      showToast('Failed to create links', 'error');
      return [];
    } finally {
      setIsLinking(false);
    }
  };

  // Delete a link
  const deleteLink = async (linkId: string): Promise<boolean> => {
    if (!user) return false;

    setIsUnlinking(true);
    try {
      const { error } = await supabase
        .from('content_links')
        .delete()
        .eq('id', linkId);

      if (error) throw error;

      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.CONTENT_LINKS}_*`);
      await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_*`);
      
      return true;
    } catch (error) {
      console.error('Error deleting link:', error);
      showToast('Failed to remove link', 'error');
      return false;
    } finally {
      setIsUnlinking(false);
    }
  };

  // Delete links by criteria
  const deleteLinksByCriteria = async (
    sourceType: string,
    sourceId: string,
    targetType?: string,
    targetId?: string
  ): Promise<boolean> => {
    if (!user) return false;

    setIsUnlinking(true);
    try {
      let query = supabase
        .from('content_links')
        .delete()
        .eq('source_type', sourceType)
        .eq('source_id', sourceId);

      if (targetType) {
        query = query.eq('target_type', targetType);
      }
      if (targetId) {
        query = query.eq('target_id', targetId);
      }

      const { error } = await query;

      if (error) throw error;

      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.CONTENT_LINKS}_*`);
      await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_*`);
      
      return true;
    } catch (error) {
      console.error('Error deleting links:', error);
      return false;
    } finally {
      setIsUnlinking(false);
    }
  };

  // Get links for a specific item
  const getLinksForItem = useCallback(async (
    itemType: string,
    itemId: string,
    direction: 'source' | 'target' | 'both' = 'both'
  ): Promise<ContentLink[]> => {
    if (!user || !itemId) return [];

    try {
      let links: ContentLink[] = [];

      if (direction === 'source' || direction === 'both') {
        const { data: sourceLinks, error: sourceError } = await supabase
          .from('content_links')
          .select('*')
          .eq('source_type', itemType)
          .eq('source_id', itemId);

        if (sourceError) throw sourceError;
        links = [...links, ...(sourceLinks || [])];
      }

      if (direction === 'target' || direction === 'both') {
        const { data: targetLinks, error: targetError } = await supabase
          .from('content_links')
          .select('*')
          .eq('target_type', itemType)
          .eq('target_id', itemId);

        if (targetError) throw targetError;
        links = [...links, ...(targetLinks || [])];
      }

      return links;
    } catch (error) {
      console.error('Error fetching links:', error);
      return [];
    }
  }, [user?.id]); // Only depend on user ID, not the entire user object

  // Check if two items are linked
  const areItemsLinked = async (
    sourceType: string,
    sourceId: string,
    targetType: string,
    targetId: string
  ): Promise<boolean> => {
    if (!user) return false;

    const { data, error } = await supabase
      .from('content_links')
      .select('id')
      .eq('source_type', sourceType)
      .eq('source_id', sourceId)
      .eq('target_type', targetType)
      .eq('target_id', targetId)
      .single();

    return !error && !!data;
  };

  // Get links for multiple items in a single query (batched)
  const getLinksForMultipleItems = useCallback(async (
    itemType: string,
    itemIds: string[],
    linkType?: 'source' | 'target' | 'both'
  ): Promise<Map<string, ContentLink[]>> => {
    if (!user || itemIds.length === 0) return new Map();

    try {
      let links: ContentLink[] = [];

      // Fetch source links if needed
      if (!linkType || linkType === 'source' || linkType === 'both') {
        const { data: sourceLinks, error: sourceError } = await supabase
          .from('content_links')
          .select('*')
          .eq('source_type', itemType)
          .in('source_id', itemIds);

        if (sourceError) throw sourceError;
        links = [...links, ...(sourceLinks || [])];
      }

      // Fetch target links if needed
      if (!linkType || linkType === 'target' || linkType === 'both') {
        const { data: targetLinks, error: targetError } = await supabase
          .from('content_links')
          .select('*')
          .eq('target_type', itemType)
          .in('target_id', itemIds);

        if (targetError) throw targetError;
        links = [...links, ...(targetLinks || [])];
      }

      // Group links by item ID
      const linkMap = new Map<string, ContentLink[]>();
      itemIds.forEach(id => linkMap.set(id, []));

      links.forEach(link => {
        const id = linkType === 'target' ? link.target_id : link.source_id;
        const existing = linkMap.get(id) || [];
        existing.push(link);
        linkMap.set(id, existing);
      });

      return linkMap;
    } catch (error) {
      console.error('Error fetching links for multiple items:', error);
      return new Map();
    }
  }, [user?.id]); // Only depend on user ID, not the entire user object

  // Get all links matching a filter
  const getFilteredLinks = useCallback(async (filter: LinkFilter): Promise<ContentLink[]> => {
    if (!user) return [];

    try {
      let query = supabase.from('content_links').select('*');

      if (filter.sourceType) {
        query = query.eq('source_type', filter.sourceType);
      }
      if (filter.sourceId) {
        query = query.eq('source_id', filter.sourceId);
      }
      if (filter.targetType) {
        query = query.eq('target_type', filter.targetType);
      }
      if (filter.targetId) {
        query = query.eq('target_id', filter.targetId);
      }
      if (filter.metadata) {
        Object.entries(filter.metadata).forEach(([key, value]) => {
          // For JSON queries, string values need to be quoted
          const quotedValue = typeof value === 'string' ? `"${value}"` : value;
          query = query.eq(`metadata->${key}`, quotedValue);
        });
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching filtered links:', error);
      return [];
    }
  }, [user?.id]); // Only depend on user ID, not the entire user object

  return {
    createLink,
    createBulkLinks,
    deleteLink,
    deleteLinksByCriteria,
    getLinksForItem,
    getLinksForMultipleItems,
    areItemsLinked,
    getFilteredLinks,
    isLinking,
    isUnlinking,
  };
}